import { router } from '@inertiajs/react';
import { formatDistance } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ProviderTabs } from '@/components/ProviderTabs';
import { workflow_run_path, workflows_path } from '@/lib/routes';
import { useWorkflowFilters } from '@/hooks/useWorkflowFilters';
import { SortableTableHead } from './components/SortableTableHead';
import { WorkflowFilters } from './components/WorkflowFilters';
import type { Workflow } from '@/types/workflow';
import { WORKFLOW_STATUSES } from '@/types/workflow';

interface Provider {
  name: string;
  display_name: string;
  color_scheme: string;
}

interface IndexProps {
  workflows: Workflow[];
  providers?: Provider[];
  currentProvider?: string;
}

const getStatusConfig = (status: number, isRunning: boolean) => {
  if (isRunning) {
    return WORKFLOW_STATUSES[1];
  }
  return WORKFLOW_STATUSES[status] || WORKFLOW_STATUSES[3];
};

const truncateId = (id: string) => {
  return id.length > 8 ? `${id.substring(0, 8)}...` : id;
};

const Index = ({ workflows, providers = [], currentProvider = 'flow' }: IndexProps) => {
  const {
    filteredAndSortedWorkflows,
    totalCount,
    filteredCount,
    sortBy,
    sortDirection,
    handleSort,
    statusFilter,
    workflowFilter,
    setStatusFilter,
    setWorkflowFilter,
    clearFilters,
  } = useWorkflowFilters(workflows);

  const handleProviderChange = (provider: string) => {
    router.visit(workflows_path({ provider }));
  };

  return (
    <div className="container mx-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Latest Workflow Executions</h1>

        <ProviderTabs
          providers={providers}
          current={currentProvider}
          onChange={handleProviderChange}
        />
      </div>

      {/* Filters */}
      <WorkflowFilters
        statusFilter={statusFilter}
        workflowFilter={workflowFilter}
        onStatusFilterChange={setStatusFilter}
        onWorkflowFilterChange={setWorkflowFilter}
        onClearFilters={clearFilters}
        totalCount={totalCount}
        filteredCount={filteredCount}
      />

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead
                column="status"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Status
              </SortableTableHead>
              <SortableTableHead
                column="name"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Workflow
              </SortableTableHead>
              <SortableTableHead
                column="startTime"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Start Date
              </SortableTableHead>
              <SortableTableHead
                column="duration"
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Duration
              </SortableTableHead>
              <TableHead>Workflow ID</TableHead>
              <TableHead>Run ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedWorkflows.map((workflow: Workflow) => {
              const statusConfig = getStatusConfig(workflow.status, workflow.isRunning);
              return (
                <TableRow
                  key={workflow.runId}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() =>
                    router.visit(
                      workflow_run_path(workflow.id, workflow.runId, {
                        provider: currentProvider,
                      })
                    )
                  }
                >
                  <TableCell>
                    <Badge className={statusConfig.color} variant="secondary">
                      {statusConfig.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{workflow.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {new Date(workflow.startTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    {workflow.endTime
                      ? formatDistance(new Date(workflow.startTime), new Date(workflow.endTime), {
                          addSuffix: false,
                        })
                      : 'In Progress'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{workflow.id}</TableCell>
                  <TableCell className="font-mono text-xs" title={workflow.runId}>
                    {truncateId(workflow.runId)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Index;
